import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card } from '../../components/common/Card';
import { AdjustConfigForm } from '../../components/adjust/AdjustConfigForm';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { adjustApi } from '../../api/adjust.api';
import { gamesApi } from '../../api/games.api';
import { AdjustConfig as AdjustConfigType } from '../../types/game.types';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/common/Button';

export const AdjustConfig: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();

  const { data: gameData } = useQuery({
    queryKey: ['game', gameId],
    queryFn: () => gamesApi.getGame(gameId!),
    enabled: !!gameId,
  });

  const { data: configData, isLoading } = useQuery({
    queryKey: ['adjust-config', gameId],
    queryFn: () => adjustApi.getAdjustConfig(gameId!),
    enabled: !!gameId,
  });

  const updateConfigMutation = useMutation({
    mutationFn: (config: AdjustConfigType) => adjustApi.updateAdjustConfig(gameId!, config),
    onSuccess: () => {
      toast.success('Adjust configuration updated successfully');
      navigate('/games');
    },
    onError: () => {
      toast.error('Failed to update Adjust configuration');
    },
  });

  if (isLoading) {
    return <LoadingSpinner message="Loading configuration..." />;
  }

  return (
    <div>
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/games')}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to Games
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Adjust Configuration</h1>
        {gameData?.data && (
          <p className="text-gray-600 mt-2">Game: {gameData.data.name}</p>
        )}
      </div>

      <Card>
        <AdjustConfigForm
          config={configData?.data}
          onSubmit={async (config) => {
            await updateConfigMutation.mutateAsync(config);
          }}
          isLoading={updateConfigMutation.isPending}
        />
      </Card>
    </div>
  );
};